import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import type { SubscriptionPlanType } from '../../types/subscriptionPlan';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import SubscriptionModal from './subscription/SubscriptionModal';
import { getUpcomingInvoice } from '../../utils/stripe/api';
import type { CurrentSubscriptionData } from './PricingPlans';
import { SUBSCRIPTION_PLANS } from '../../utils/stripe/config';

interface PricingCardProps {
  plan: SubscriptionPlanType;
  currentSubscriptionData: CurrentSubscriptionData | null;
  isSelected?: boolean;
  isLoading?: boolean;
  isNext?: boolean;
  isExistedNext?: boolean;
  isDisabled?: boolean;
}



export function PricingCard({ plan, currentSubscriptionData, isSelected = false, isNext=false, isExistedNext=false, isLoading = false, isDisabled = false }: PricingCardProps) {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const user = useAuthStore(state => state.user);
  const [nextPaymentAttempt, setNextPaymentAttempt] = useState<number | null>(null);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const currentPlan = currentSubscriptionData?.planId
    ? SUBSCRIPTION_PLANS.find((p) => p.id === currentSubscriptionData.planId)
    : null;
  const isBlockedByPendingDowngrade =
    isExistedNext && !isNext && !isSelected && !!currentPlan && plan.price < currentPlan.price;

  // Fetch current subscription ID when component mounts
  useEffect(() => {
    const fetchSubscriptionId = async () => {
      if (!user?.id) return;

      if (isNext) {
        // Fetch subscription details from Stripe via your backend API
        try {
          const upcomingInvoice = await getUpcomingInvoice(user.id);
          setNextPaymentAttempt(upcomingInvoice?.next_payment_attempt || null);
        } catch (stripeErr) {
          console.error('Failed to fetch Stripe subscription details:', stripeErr);
        }
      }
    };
    
    fetchSubscriptionId();
  }, [user?.id, isNext]);

  const handleSubscribe = () => {
    if(isSelected) return;

    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }
    
    setShowSubscriptionModal(true);
  };

  return (
    <div
      className={`relative bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
        isSelected && !isLoading
          ? currentSubscriptionData?.status === 'past_due'
            ? 'ring-4 ring-gray-400 ring-opacity-50 scale-105 shadow-xl'
            : 'ring-4 ring-blue-500 ring-opacity-50 scale-105 shadow-xl'
          : 'hover:scale-102 hover:shadow-xl'
      }
      ${
        isNext
          ? 'ring-4 ring-green-500 ring-opacity-50 scale-105 shadow-xl' 
          : 'hover:scale-102 hover:shadow-xl'
      }   
      `}
    >
      {isSelected && !isLoading && (
        <div className={`absolute top-0 left-0 right-0 h-1 ${
          currentSubscriptionData?.status === 'past_due'
            ? 'bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600'
            : 'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700'
        }`} />
      )}
      
      <div className="p-6">
        <div className="mb-4">
            {isSelected && (
              <div className="absolute top-1 right-0 z-10">
              {currentSubscriptionData?.status === 'past_due' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <Check className="w-4 h-4 mr-1" />
                支払い未完了
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Check className="w-4 h-4 mr-1" />
                選択中
                </span>
              )}
              </div>
            )}
            <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
        </div>

        <div className="flex items-baseline mb-6">
          <span className="text-4xl font-bold text-gray-900">{plan.price.toLocaleString()}</span>
          <span className="text-gray-600 ml-1">円/月</span>
        </div>

        <ul className="space-y-4 mb-8">
          {plan.features?.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-600">{feature}</span>
            </li>
          )) || (
            <>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">基本機能</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">
                  AIタイトル生成 月{plan.aiLimit.toLocaleString()}回まで
                </span>
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="px-6 pb-6">
        <button
          onClick={handleSubscribe}
          disabled={isDisabled || isBlockedByPendingDowngrade || isSelected || isLoading}
          className={`w-full py-3 px-4 rounded-md font-medium transition-all duration-300 ${
            (isDisabled || isBlockedByPendingDowngrade || isSelected || isLoading)
              ? 'bg-blue-300 text-white'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 hover:shadow-md'
          }`}
        >

          {isSelected && '選択中のプラン'}
          {!isSelected && isDisabled && 'プランのキャンセル手順...'}
          {!isSelected && !isDisabled && isLoading && '読み込み中...'}
          {!isSelected && !isDisabled && !isLoading && isBlockedByPendingDowngrade && 'プランダウングレード手順...'}
          {!isSelected && !isDisabled && !isLoading && !isBlockedByPendingDowngrade && isExistedNext && isNext && '次のプランを選択中'}
          {!isSelected && !isDisabled && !isLoading && !isBlockedByPendingDowngrade && !(isExistedNext && isNext) && 'このプランを選択する'}
          
        </button>
      </div>

      {isNext && nextPaymentAttempt && (
        <div className="absolute top-0 right-0 bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-bl-lg">
          次のプランは {new Date(nextPaymentAttempt * 1000).toLocaleDateString()} に開始
        </div>
      )}

     {isDisabled && isSelected && (
      <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center px-4 pointer-events-none">
        <p className="text-center text-gray-700 font-medium">
        現在のプランはキャンセル手続き中です。次回の請求期間終了後に新しいプランを選択できます。
        </p>
        {currentSubscriptionData?.currentPeriodEnd && (
        <p className="text-center text-red-500 text-sm mt-2">
          現在のプランは {new Date(currentSubscriptionData.currentPeriodEnd * 1000).toLocaleDateString()} に終了します。
        </p>
        )}
      </div>
      )}

      {showSubscriptionModal && (
          <SubscriptionModal
            plan={plan}
            currentPlanId={currentSubscriptionData?.planId || null}
            currentSubscriptionId={currentSubscriptionData?.id || null}
            onClose={() => setShowSubscriptionModal(false)}
          />
      )}
    </div>
  );
}